import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress, Typography, Box } from '@material-ui/core'
import Map from './components/map/Map'
import { useJobGeoJSON } from './adapters/publicApi'
// import { useLayers } from '@graniot/common/context/LayersContext'

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
    width: '100vw',
    position: 'relative',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column',
  },
  error: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
}))

function PublicMapApp() {
  const classes = useStyles()
  const [searchParams] = useSearchParams()
  const [map, setMap] = React.useState(null)
  // const { layers } = useLayers()
  // Provide default Parcels layer so they are visible
  const layers = [
    { name: 'Parcels', visible: true }
  ]

  // Get URL parameters
  const jobUuid = searchParams.get('job')
  const authPk = searchParams.get('auth_pk')

  // Store API key and redirect to clean URL
  React.useEffect(() => {
    if (authPk) {
      // Store the API key
      localStorage.setItem('graniot_public_api_key', authPk)

      // Redirect to clean URL without auth_pk
      const newUrl = new URL(window.location)
      newUrl.searchParams.delete('auth_pk')
      window.history.replaceState({}, document.title, newUrl.toString())
    }
  }, [authPk])

  // Fetch job data
  const { data: jobData, isLoading, error } = useJobGeoJSON(jobUuid)

  // Transform job data to farm format for the map
  const farms = React.useMemo(() => {
    if (!jobData) return []

    const totalHectares = jobData.features.reduce((sum, f) =>
      sum + (f.properties.hectares || 0), 0
    )

    const farmData = [{
      id: `job-${jobUuid}`,
      name: `Job ${jobUuid?.substring(0, 8)}`,
      checked: true,
      farm_parcels: jobData,
      total_hectares: { "1": totalHectares }
    }]

    return farmData
  }, [jobData, jobUuid])

  // Check for API key in localStorage if not in URL
  const hasApiKey = authPk || localStorage.getItem('graniot_public_api_key')

  // Handle missing parameters
  if (!jobUuid) {
    return (
      <div className={classes.error}>
        <Typography variant="h5" color="error">
          Missing Job ID
        </Typography>
        <Typography variant="body1" style={{ marginTop: 16 }}>
          Please provide the 'job' parameter in the URL
        </Typography>
      </div>
    )
  }

  if (!hasApiKey) {
    return (
      <div className={classes.error}>
        <Typography variant="h5" color="error">
          Missing API Key
        </Typography>
        <Typography variant="body1" style={{ marginTop: 16 }}>
          Please provide the 'auth_pk' parameter in the URL
        </Typography>
      </div>
    )
  }

  // Handle loading
  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginTop: 16 }}>
          Loading job data...
        </Typography>
      </div>
    )
  }

  // Handle error
  if (error) {
    return (
      <div className={classes.error}>
        <Typography variant="h5" color="error">
          Error Loading Data
        </Typography>
        <Typography variant="body1" style={{ marginTop: 16 }}>
          {error.message}
        </Typography>
      </div>
    )
  }

  // Render the map
  return (
    <div className={classes.root}>
      <Map
        farms={farms}
        companyId={jobData?.company_id}
        isLoadingFarms={false}
        isPublicMode={true}
        layers={layers}
        setMap={setMap}
        map={map}
        // Simplified props - no editing features
        openDrawer={false}
        setOpenDrawer={() => {}}
        openAddParcel={{ open: false }}
        setOpenAddParcel={() => {}}
        dispatchAddedParcels={() => {}}
        addedParcels={[]}
        dispatchFarms={() => {}}
        numberOfParcels={jobData?.features?.length || 0}
        farmsHectares={farms[0]?.total_hectares?.["1"] || 0}
        drawerWidth={0}
        plantsCounter={0}
        openDrawerCustomLayer={false}
        setOpenDrawerCustomLayer={() => {}}
      />
    </div>
  )
}

export default PublicMapApp
